import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { parseLinks } from '../utils/parseLinks';
import { ICONS } from '../utils/icons';
import { Check } from 'lucide-react';

interface TariffFeature {
  icon?: string;
  text: string;
}

interface SiteSettings {
  basic_plan_name: string;
  basic_price: number;
  basic_period_label: string;
  basic_description: string | null;
  basic_features_json: string | null;
  basic_recurring_terms: string | null;

  trial_plan_name: string;
  trial_price: number;
  trial_period_label: string;
  trial_description: string | null;
  trial_features_json: string | null;
  trial_recurring_terms: string | null;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const parseFeatures = (json: string | null): TariffFeature[] => {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

interface TariffCardProps {
  planName: string;
  price: number;
  periodLabel: string;
  description: string | null;
  features: TariffFeature[];
  recurringText: string | null;
}

const TariffCard: React.FC<TariffCardProps> = ({
  planName,
  price,
  periodLabel,
  description,
  features,
  recurringText,
}) => (
  <div className="bg-lunar-800/50 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-[var(--primary-color-800-30)]">
    <div className="flex items-start justify-between gap-4 mb-6">
      <div className="min-w-0">
        <h2 className="text-2xl font-bold text-white mb-1">{planName}</h2>
        {description && <p className="text-lunar-400 text-sm">{description}</p>}
      </div>
      <div className="text-right flex-shrink-0">
        <div className="text-3xl sm:text-4xl font-bold text-white whitespace-nowrap">
          {price} ₽
        </div>
        <div className="text-lunar-400 text-xs sm:text-sm">{periodLabel}</div>
      </div>
    </div>

    {features.length > 0 && (
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mb-6">
        {features.map((f, i) => (
          <div key={i} className="flex items-start text-lunar-200 text-sm">
            <Check className="w-4 h-4 text-mystic-400 mr-2 mt-0.5 flex-shrink-0" />
            <span>{f.text}</span>
          </div>
        ))}
      </div>
    )}

    {recurringText && (
      <div className="pt-5 border-t border-[var(--primary-color-800-30)] text-sm text-lunar-300 leading-relaxed">
        <span className="font-semibold text-white">Условия автопродления:</span>{' '}
        {parseLinks(recurringText)}
      </div>
    )}
  </div>
);

const Tariffs: React.FC = () => {
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [status, setStatus] = useState<'loading' | 'ok' | 'error'>('loading');

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API_URL}/api/site-settings`)
      .then(async (r) => {
        if (!r.ok) throw new Error('failed');
        const data = (await r.json()) as SiteSettings;
        setSettings(data);
        setStatus('ok');
      })
      .catch(() => setStatus('error'));
  }, []);

  return (
    <div className="min-h-screen bg-lunar-gradient py-20 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3">Тарифы</h1>
        <p className="text-lunar-400 mb-10 max-w-2xl">
          Актуальные тарифы сервиса. Стоимость указана в российских рублях, включая НДС.
        </p>

        {status === 'loading' && (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-mystic-400 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {status === 'error' && (
          <p className="text-lunar-300">Не удалось загрузить тарифы. Попробуйте позже.</p>
        )}

        {status === 'ok' && settings && (
          <div className="space-y-6">
            <TariffCard
              planName={settings.basic_plan_name}
              price={settings.basic_price}
              periodLabel={settings.basic_period_label}
              description={settings.basic_description}
              features={parseFeatures(settings.basic_features_json)}
              recurringText={settings.basic_recurring_terms}
            />
            <TariffCard
              planName={settings.trial_plan_name}
              price={settings.trial_price}
              periodLabel={settings.trial_period_label}
              description={settings.trial_description}
              features={parseFeatures(settings.trial_features_json)}
              recurringText={settings.trial_recurring_terms}
            />
          </div>
        )}

        <div className="mt-10 space-y-3 text-sm text-lunar-500 leading-relaxed">
          <p>
            Услуги оказываются на основании{' '}
            <Link to="/terms-of-service" className="text-mystic-400 hover:text-mystic-300 underline">
              Договора-оферты
            </Link>{' '}
            и{' '}
            <Link to="/privacy-policy" className="text-mystic-400 hover:text-mystic-300 underline">
              Политики конфиденциальности
            </Link>
            . Оформление подписки означает согласие с условиями этих документов и с{' '}
            <Link to="/auto-payments" className="text-mystic-400 hover:text-mystic-300 underline">
              Соглашением об автоплатежах
            </Link>
            .
          </p>
          <p>
            Платежи обрабатываются ООО «КлаудПэйментс» — лицензированным оператором электронных
            денежных средств, сертифицированным по PCI DSS Level 1.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Tariffs;
